import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Mail, Package, RefreshCw, LogOut, Download } from 'lucide-react';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { LinkButtons, PwBox } from './GuidePages';
import { RelocationDownloads } from '../components/shop/RelocationDownloads';
import { UpgradeOffer } from '../components/shop/UpgradeOffer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'tes_customer_token';
const errMsg = (e, fallback) => e?.response?.data?.detail || fallback;

const EmailStep = ({ email, setEmail, onSent }) => {
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/orders/lookup/request-code`, { email });
      toast.success('Check your inbox for a 6-digit code');
      onSent();
    } catch (err) { toast.error(errMsg(err, 'Please enter a valid email address')); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="lookup-email-form">
      <label className="block text-sm text-[#1E1622]/70">The email you used at checkout</label>
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
        data-testid="lookup-email-input" className="input-editorial w-full" />
      <button type="submit" disabled={busy} data-testid="lookup-send-code-btn" className="btn-gold inline-flex items-center gap-2">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />} Email me a code
      </button>
    </form>
  );
};

const CodeStep = ({ email, onVerified, onBack }) => {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const verify = async (value) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/orders/lookup/verify`, { email, code: value });
      sessionStorage.setItem(TOKEN_KEY, r.data.token);
      onVerified();
    } catch (err) { toast.error(errMsg(err, 'That code did not work')); setCode(''); }
    setBusy(false);
  };
  return (
    <div className="space-y-5" data-testid="lookup-code-step">
      <p className="text-sm text-[#1E1622]/70">If we have orders for <span className="font-medium text-[#1E1622]">{email}</span>, we've sent a 6-digit code. It expires in 15 minutes.</p>
      <InputOTP maxLength={6} value={code} onChange={setCode} onComplete={verify} disabled={busy} data-testid="lookup-code-input">
        <InputOTPGroup>
          {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-12 w-11 text-lg bg-white border-[#E6DAC5]" />)}
        </InputOTPGroup>
      </InputOTP>
      <div className="flex items-center gap-5">
        <button onClick={() => verify(code)} disabled={busy || code.length !== 6} data-testid="lookup-verify-btn" className="btn-gold inline-flex items-center gap-2">
          {busy && <Loader2 size={16} className="animate-spin" />} View my orders
        </button>
        <button onClick={onBack} data-testid="lookup-change-email-btn" className="text-sm link-gold">Use a different email</button>
      </div>
    </div>
  );
};

const OrderRow = ({ order, onFresh }) => {
  const [busy, setBusy] = useState(false);
  const [links, setLinks] = useState(null);
  const fresh = async () => {
    setBusy(true);
    try { setLinks(await onFresh(order.id)); } catch (err) { toast.error(errMsg(err, 'Could not get fresh links')); }
    setBusy(false);
  };
  const copyPw = (pw) => { navigator.clipboard.writeText(pw); toast.success('Password copied'); };
  return (
    <div className="bg-white rounded-2xl border border-[#E6DAC5] p-5" data-testid={`my-order-${order.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-medium text-[#1E1622]">{order.product_title}</p>
          <p className="text-xs text-[#1E1622]/50">{new Date(order.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })} · A${order.amount}</p>
        </div>
        {['studio', 'relocation'].includes(order.order_type) ? (
          <button onClick={fresh} disabled={busy} data-testid={`fresh-links-${order.id}`} className="btn-secondary text-sm inline-flex items-center gap-2">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} {order.order_type === 'relocation' ? 'Get fresh download links' : 'Get fresh links'}
          </button>
        ) : order.has_file && (
          <a href={`${API}/orders/${order.id}/download`} data-testid={`download-${order.id}`} className="btn-secondary text-sm inline-flex items-center gap-2"><Download size={14} /> Download</a>
        )}
      </div>
      {links?.items && (
        <div className="mt-5 pt-5 border-t border-[#E6DAC5]" data-testid={`fresh-links-panel-${order.id}`}>
          <RelocationDownloads download={links} compact />
        </div>
      )}
      {links && !links.items && (
        <div className="mt-5 pt-5 border-t border-[#E6DAC5] space-y-4" data-testid={`fresh-links-panel-${order.id}`}>
          {links.guides ? links.guides.map((g, i) => (
            <div key={i}><p className="text-sm font-medium text-[#1E1622] mb-2">{g.town}</p><LinkButtons pdf_url={g.pdf_url} app_url={g.app_url} /></div>
          )) : <LinkButtons pdf_url={links.pdf_url} app_url={links.app_url} />}
          {links.pdf_password && <PwBox pw={links.pdf_password} onCopy={copyPw} />}
          <p className="text-xs text-[#1E1622]/50">
            {links.expires_at && <>Links expire on {new Date(links.expires_at).toLocaleString('en-AU')}. </>}
            {links.max_downloads && <>Up to {links.max_downloads} downloads.</>}
          </p>
        </div>
      )}
    </div>
  );
};

const OrdersList = ({ onSignOut }) => {
  const [data, setData] = useState(null);
  const headers = { headers: { Authorization: `Bearer ${sessionStorage.getItem(TOKEN_KEY)}` } };
  useEffect(() => {
    axios.get(`${API}/orders/mine`, headers).then((r) => setData(r.data)).catch(() => onSignOut());
    // eslint-disable-next-line
  }, []);
  const onFresh = async (id) => (await axios.post(`${API}/orders/mine/${id}/fresh-links`, {}, headers)).data.download;
  if (!data) return <Loader2 className="animate-spin text-[#3B164C]" size={32} />;
  return (
    <div className="space-y-4" data-testid="my-orders-list">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm text-[#1E1622]/60">Signed in as <span className="font-medium text-[#1E1622]">{data.email}</span></p>
        <button onClick={onSignOut} data-testid="lookup-sign-out-btn" className="text-sm link-gold inline-flex items-center gap-1"><LogOut size={14} /> Sign out</button>
      </div>
      {data.orders.length === 0 ? (
        <div className="text-center py-10" data-testid="my-orders-empty">
          <Package className="mx-auto text-[#C5A059] mb-3" size={32} />
          <p className="text-[#1E1622]/60 mb-4">No completed orders for this email yet.</p>
          <Link to="/shop" className="btn-gold text-sm">Browse the guides</Link>
        </div>
      ) : (<>
        {(data.upgrades || []).map((q) => <UpgradeOffer key={q.guide_id} quote={q} headers={headers} />)}
        {data.orders.map((o) => <OrderRow key={o.id} order={o} onFresh={onFresh} />)}
      </>)}
    </div>
  );
};

export default function MyOrdersPage() {
  const [step, setStep] = useState(sessionStorage.getItem(TOKEN_KEY) ? 'orders' : 'email');
  const [email, setEmail] = useState('');
  const signOut = () => { sessionStorage.removeItem(TOKEN_KEY); setStep('email'); };
  return (
    <div className="min-h-screen pt-28 pb-20 bg-[#FAF7F2]" data-testid="my-orders-page">
      <div className="container-custom max-w-2xl">
        <span className="eyebrow mb-4"><span className="w-10 h-px bg-[#C5A059]" /> Your purchases</span>
        <h1 className="font-display text-4xl sm:text-5xl font-medium text-[#1E1622] mb-3">My Orders</h1>
        <p className="text-[#1E1622]/60 font-light mb-10">See your guides and get fresh download links at any time. No account or password needed.</p>
        <div className="bg-[#F5EFE9] rounded-3xl border border-[#E6DAC5] p-6 md:p-10">
          {step === 'email' && <EmailStep email={email} setEmail={setEmail} onSent={() => setStep('code')} />}
          {step === 'code' && <CodeStep email={email} onVerified={() => setStep('orders')} onBack={() => setStep('email')} />}
          {step === 'orders' && <OrdersList onSignOut={signOut} />}
        </div>
      </div>
    </div>
  );
}
